/**
 * React Query Hooks for Expiring Contracts
 * US-CONT-005: View Expiring Contracts Alert
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { contractsApi, contractQueryKeys } from '../api/contractsApi';
import { enrichContractsWithUrgency } from '../utils/contractUrgency.utils';
import type {
  ExpiringContractsQueryParams,
  ExpiringContractWithUrgency,
  ExpiringContractsSummary,
  ExpiringContract,
  RenewalRequest,
  RenewalResponse
} from '../types/expiringContracts.types';
import {
  STALE_TIME,
  CACHE_TIME,
  REFETCH_INTERVAL,
  DEFAULT_PAGE_SIZE,
  DEFAULT_DAYS_THRESHOLD,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES
} from '../constants/expiringContracts.constants';

/**
 * Hook to fetch expiring contracts with urgency calculation
 * @param params - Query parameters
 * @returns Query result with enriched contracts
 */
export function useExpiringContracts(params: Partial<ExpiringContractsQueryParams> = {}) {
  return useQuery({
    queryKey: contractQueryKeys.expiringList(params),
    queryFn: async () => {
      const response = await contractsApi.getExpiringContracts({
        page: 0,
        size: DEFAULT_PAGE_SIZE,
        days: DEFAULT_DAYS_THRESHOLD,
        ...params
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || ERROR_MESSAGES.FETCH_FAILED);
      }

      // Enrich contracts with urgency metadata
      const enrichedContent = enrichContractsWithUrgency(response.data.content);

      return {
        ...response.data,
        content: enrichedContent
      };
    },
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    retry: 2,
    retryDelay: 1000
  });
}

/**
 * Hook to fetch expiring contracts summary for dashboard
 * @param days - Number of days threshold
 * @param options - Additional query options
 * @returns Query result with summary data
 */
export function useExpiringContractsSummary(
  days: number = DEFAULT_DAYS_THRESHOLD,
  options?: { enabled?: boolean; refetchInterval?: number }
) {
  return useQuery<ExpiringContractsSummary>({
    queryKey: contractQueryKeys.expiringSummary(days),
    queryFn: async () => {
      const response = await contractsApi.getExpiringContractsSummary(days);

      if (!response.success || !response.data) {
        throw new Error(response.error || ERROR_MESSAGES.FETCH_FAILED);
      }

      return response.data;
    },
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    refetchInterval: options?.refetchInterval || REFETCH_INTERVAL,
    enabled: options?.enabled !== false,
    retry: 2
  });
}

/**
 * Hook to fetch single contract details
 * @param contractId - Contract ID
 * @param options - Additional query options
 * @returns Query result with contract details
 */
export function useContractDetails(
  contractId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<ExpiringContractWithUrgency>({
    queryKey: contractQueryKeys.detail(contractId || ''),
    queryFn: async () => {
      if (!contractId) {
        throw new Error('Contract ID is required');
      }

      const response = await contractsApi.getContractById(contractId);

      if (!response.success || !response.data) {
        throw new Error(response.error || ERROR_MESSAGES.FETCH_FAILED);
      }

      // Enrich with urgency metadata
      const [enrichedContract] = enrichContractsWithUrgency([response.data]);
      return enrichedContract;
    },
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    enabled: options?.enabled !== false && !!contractId,
    retry: 2
  });
}

/**
 * Hook to renew a contract
 * @returns Mutation for renewing contract
 */
export function useRenewContract() {
  const queryClient = useQueryClient();

  return useMutation<RenewalResponse, Error, RenewalRequest>({
    mutationFn: async (request: RenewalRequest) => {
      const response = await contractsApi.initiateRenewal(request);

      if (!response.success || !response.data) {
        throw new Error(response.error || ERROR_MESSAGES.RENEW_FAILED);
      }

      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate all expiring contracts queries
      queryClient.invalidateQueries({ queryKey: contractQueryKeys.expiring() });

      // Invalidate specific contract detail
      queryClient.invalidateQueries({ queryKey: contractQueryKeys.detail(variables.contractId) });

      // Show success toast
      toast.success(SUCCESS_MESSAGES.RENEW_SUCCESS, {
        description: `New contract: ${data.newContractNumber}`
      });
    },
    onError: (error: Error) => {
      toast.error(ERROR_MESSAGES.RENEW_FAILED, {
        description: error.message
      });
    }
  });
}

/**
 * Hook to download contract
 * @returns Mutation for downloading contract
 */
export function useDownloadContract() {
  return useMutation<Blob, Error, string>({
    mutationFn: async (contractId: string) => {
      return await contractsApi.downloadContract(contractId);
    },
    onSuccess: () => {
      toast.success(SUCCESS_MESSAGES.DOWNLOAD_SUCCESS);
    },
    onError: (error: Error) => {
      toast.error(ERROR_MESSAGES.DOWNLOAD_FAILED, {
        description: error.message
      });
    }
  });
}

/**
 * Hook to prefetch contract details
 * Useful for hover states on table rows
 * @returns Prefetch function
 */
export function usePrefetchContractDetails() {
  const queryClient = useQueryClient();

  return async (contractId: string) => {
    await queryClient.prefetchQuery({
      queryKey: contractQueryKeys.detail(contractId),
      queryFn: async () => {
        const response = await contractsApi.getContractById(contractId);

        if (!response.success || !response.data) {
          throw new Error(response.error || ERROR_MESSAGES.FETCH_FAILED);
        }

        const [enrichedContract] = enrichContractsWithUrgency([response.data]);
        return enrichedContract;
      },
      staleTime: STALE_TIME
    });
  };
}
